// public/js/features/feedback/uploader.js
// Attachment bar: paste a screenshot, pick image files, or add an image URL
// (re-hosted server-side). Returns the uploaded attachment list.
import { uploadImage } from './api.js';

function blobToB64(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] || '');
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

export function createAttachmentBar() {
  const pending = []; // { key, name, contentType, _url(preview) }
  const el = document.createElement('div');
  el.className = 'fb-attach-bar mt-2';
  el.innerHTML = `
    <div class="fb-thumbs d-flex flex-wrap gap-2 mb-2"></div>
    <div class="d-flex gap-2 align-items-center small flex-wrap">
      <label class="btn btn-sm btn-outline-secondary mb-0">
        <i class="fa-solid fa-image me-1"></i>Image<input type="file" accept="image/*" multiple hidden class="fb-file">
      </label>
      <button type="button" class="btn btn-sm btn-outline-secondary fb-url-btn"><i class="fa-solid fa-link me-1"></i>From URL</button>
      <span class="text-muted">or paste a screenshot</span>
      <span class="fb-status text-muted ms-auto"></span>
    </div>
    <div class="input-group input-group-sm mt-2 fb-url-row d-none">
      <input type="url" class="form-control fb-url-input" placeholder="https://…image.png">
      <button type="button" class="btn btn-outline-primary fb-url-add">Add</button>
    </div>`;

  const thumbs = el.querySelector('.fb-thumbs');
  const status = el.querySelector('.fb-status');
  const setStatus = (t, err) => { status.textContent = t || ''; status.classList.toggle('text-danger', !!err); };

  function render() {
    thumbs.innerHTML = pending.map((a, i) => `
      <div class="position-relative" style="width:64px;height:64px;">
        <img src="${a._url}" alt="" style="width:64px;height:64px;object-fit:cover;border-radius:6px;border:1px solid #dee2e6;">
        <button type="button" data-i="${i}" class="fb-rm position-absolute top-0 end-0 btn btn-danger p-0 lh-1" style="width:18px;height:18px;font-size:11px;border-radius:50%;">×</button>
      </div>`).join('');
    thumbs.querySelectorAll('.fb-rm').forEach(b => b.addEventListener('click', () => {
      const i = +b.dataset.i; const a = pending[i];
      if (a?._url?.startsWith('blob:')) URL.revokeObjectURL(a._url);
      pending.splice(i, 1); render();
    }));
  }

  async function addBlob(blob, name) {
    if (!blob) return;
    if (pending.length >= 12) { setStatus('Max 12 images', true); return; }
    setStatus('Uploading…');
    try {
      const dataBase64 = await blobToB64(blob);
      const att = await uploadImage({ dataBase64, contentType: blob.type, filename: name || 'image' });
      att._url = URL.createObjectURL(blob);
      pending.push(att); render(); setStatus('');
    } catch (e) { setStatus(e.message || 'Upload failed', true); }
  }

  async function addUrl(url) {
    if (!url) return;
    setStatus('Fetching…');
    try {
      const att = await uploadImage({ url });
      att._url = url; // external preview (display only)
      pending.push(att); render(); setStatus('');
    } catch (e) { setStatus(e.message || 'Could not add URL', true); }
  }

  el.querySelector('.fb-file').addEventListener('change', (e) => {
    [...e.target.files].forEach(f => addBlob(f, f.name));
    e.target.value = '';
  });
  el.querySelector('.fb-url-btn').addEventListener('click', () => el.querySelector('.fb-url-row').classList.toggle('d-none'));
  el.querySelector('.fb-url-add').addEventListener('click', () => {
    const inp = el.querySelector('.fb-url-input');
    addUrl(inp.value.trim()); inp.value = '';
  });

  return {
    el,
    // Bind clipboard-image paste on a textarea.
    bindPaste(textarea) {
      textarea.addEventListener('paste', (e) => {
        const items = [...(e.clipboardData?.items || [])];
        const imgs = items.filter(it => it.type.startsWith('image/'));
        if (imgs.length) { e.preventDefault(); imgs.forEach(it => addBlob(it.getAsFile(), 'pasted.png')); }
      });
    },
    getAttachments() { return pending.map(({ key, name, contentType }) => ({ key, name, contentType })); },
    clear() { pending.forEach(a => a._url?.startsWith('blob:') && URL.revokeObjectURL(a._url)); pending.length = 0; render(); },
  };
}
